import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import {
  FacilityStatus,
  FACILITY_STATUS_LABELS
} from '@umss/shared/data-access';
import { BadgeVariant } from '@umss/shared/utils';
import { UmssBadgeComponent } from '../badge/badge.component';

const FACILITY_STATUS_VARIANT: Record<FacilityStatus, BadgeVariant> = {
  [FacilityStatus.Active]: 'success',
  [FacilityStatus.Inactive]: 'warning',
  [FacilityStatus.PendingApproval]: 'secondary',
  [FacilityStatus.Suspended]: 'error',
  [FacilityStatus.Rejected]: 'error'
};

/**
 * Component to display a badge based on facility status.
 * Shows the status label (e.g., "Active", "Pending approval") with the
 * appropriate color variant.
 *
 * Keyed off the enum rather than a template `@switch` on string literals, so a
 * status the API renames stops compiling here instead of quietly falling
 * through to a neutral badge showing its raw name.
 */
@Component({
  selector: 'umss-facility-status-badge',
  standalone: true,
  imports: [UmssBadgeComponent],
  template: `
    <umss-badge [variant]="variant" [label]="label" [size]="size" />
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UmssFacilityStatusBadgeComponent {
  private _status?: FacilityStatus | null;

  @Input() size: 'small' | 'medium' | 'large' = 'medium';

  /**
   * Accepts null so a caller can bind straight to a facility that has not
   * loaded yet, rather than guarding the badge at every call site.
   */
  @Input({ required: true }) set status(
    value: FacilityStatus | null | undefined
  ) {
    this._status = value;
    this.label = (value && FACILITY_STATUS_LABELS[value]) || 'N/A';
    this.variant = (value && FACILITY_STATUS_VARIANT[value]) || 'neutral';
  }
  get status(): FacilityStatus | null | undefined {
    return this._status;
  }

  label = 'N/A';
  variant: BadgeVariant = 'neutral';
}
